import {Component, inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import { AuthenticationService } from '@core/auth/authentication.service';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {ContactosService} from '../contactos.service';
import {ErrorService} from '../../../../../../shared/services/error.service';
import {SnackbarService} from '@shared/services/snackbar.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {LabelComponent} from '../../../../../../shared/components/form/label/label.component';
import {FormStatusService} from '../../../../../../shared/services/form-status.service';
import {EditionButtons} from '../../../../../../shared/components/ui/edition-buttons/edition-buttons';
import {DatePipe} from '@angular/common';
import {GenericCombo} from '../../../../../../shared/services/interfaces/combos.interface';
import {CombosService} from '../../../../../../shared/services/combos.service';
import {forkJoin} from 'rxjs';
import {Modal} from '@shared/components/common/modal/modal';
import {SelectFieldComponent, SelectOption} from '@shared/components/form/select/select-field.component';
import { AppFormComponent } from '@shared/components/ui/list-components';
import {TextareaFieldComponent} from '@shared/components/form/textarea/textarea-field.component';

@Component({
  selector: 'app-detalle-contacto',
  imports: [
    HasPermissionDirective,
    LabelComponent,
    ReactiveFormsModule,
    EditionButtons,
    DatePipe,
    Modal,
    SelectFieldComponent,
    TextareaFieldComponent,

    AppFormComponent
],
  providers: [DatePipe],
  templateUrl: './detalle-contacto.html',
  styles: ``
})
export class DetalleContacto implements OnInit {
  readonly permissionAuth = inject(AuthenticationService);

  editForm!: FormGroup;
  loading = false;
  editable = true;
  updating = false;
  idProspecto!: number;
  idContacto!: number;
  userName: string | null = null;
  userModName: string | null = null;
  updatedAt: string | null = null;
  canalesContactos: GenericCombo[] = [];
  motivosContacto: GenericCombo[] = [];
  campanhas: GenericCombo[] = [];
  estadosContactos: GenericCombo[] = [];

  get canalesContactosOptions(): SelectOption[] { return this.canalesContactos.map(x => ({ value: x.id, label: x.nombre })); }
  get motivosContactoOptions(): SelectOption[] { return this.motivosContacto.map(x => ({ value: x.id, label: x.nombre })); }
  get campanhasOptions(): SelectOption[] { return this.campanhas.map(x => ({ value: x.id, label: x.nombre })); }
  get estadosContactosOptions(): SelectOption[] { return this.estadosContactos.map(x => ({ value: x.id, label: x.nombre })); }

  private fB = inject(FormBuilder);
  private fS = inject(FormStatusService);
  private contactosService = inject(ContactosService);
  private combosService = inject(CombosService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private datePipe = inject(DatePipe);
  private dialogRef = inject(MatDialogRef<DetalleContacto>);
  private data = inject(MAT_DIALOG_DATA);

  ngOnInit(): void {
    this.idProspecto = this.data.idProspecto;
    this.idContacto = this.data.idContacto;
    this.getCombos();
    this.makeForm();
    this.getContacto();
  }

  private getCombos(): void {
    const canalesContactos = this.combosService.getCanalContactoCombo();
    const motivosContacto = this.combosService.getMotivoContactoCombo();
    const campanhas = this.combosService.getCampanhasCombo();
    const estadosContactos = this.combosService.getEstadoContactoCombo();

    forkJoin([canalesContactos, motivosContacto, campanhas, estadosContactos]).subscribe({
      next: (response) => {
        this.canalesContactos = response[0].data;
        this.motivosContacto = response[1].data;
        this.campanhas = response[2].data;
        this.estadosContactos = response[3].data;
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  private makeForm(): void {
    this.editForm = this.fB.group({
      crm_prospecto_id: [{value: this.idProspecto, disabled: this.editable}, Validators.required],
      param_canal_contacto_id: [{value: '', disabled: this.editable}, Validators.required],
      param_motivo_contacto_id: [{value: '', disabled: this.editable}, Validators.required],
      crm_campanha_id: [{value: '', disabled: this.editable}, Validators.required],
      param_estado_contacto_id: [{value: '', disabled: this.editable}, Validators.required],
      fecha: [{value: '', disabled: this.editable}, Validators.required],
      resultado: [{value: '', disabled: this.editable}, Validators.required],
    });
  }

  getContacto(): void {
    this.loading = true;
    this.contactosService.show(this.idContacto).subscribe({
      next: (response) => {
        if (response.status) {
          const fecha = this.datePipe.transform(response.data.fecha, 'yyyy-MM-ddTHH:mm');
          this.editForm.patchValue({...response.data, fecha});
          this.userName = response.data.user_name ?? null;
          this.userModName = response.data.user_mod_name ?? null;
          this.updatedAt = response.data.updated_at ?? null;
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  update(): void {
    this.updating = true;
    const payload = {
      ...this.editForm.value,
      fecha: this.normalizeFechaParaApi(this.editForm.get('fecha')?.value)
    };
    this.contactosService.update(this.idContacto, payload).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Contacto actualizado correctamente');
          this.dialogRef.close(true);
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.updating = false;
    });
  }

  editableSwitcher(): void {
    this.fS.changeStatus(this.editForm, this.editable);
    this.editable = !this.editable;
  }

  refreshContacto(): void {
    this.editableSwitcher();
    this.getContacto();
  }

  close(): void {
    this.dialogRef.close();
  }

  private normalizeFechaParaApi(value: string | Date | null | undefined): string | null {
    if (!value) return null;
    if (typeof value === 'string') {
      return value.replace('T', ' ');
    }
    return this.datePipe.transform(value, 'yyyy-MM-dd HH:mm');
  }
}
